import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  Trophy,
  Dices,
  Send, 
  Twitter, 
  Music2,
  Play,
  Eye,
  History,
  Check,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORM_INFO = {
  telegram: { name: 'Telegram', icon: Send, color: 'text-blue-400' },
  twitter: { name: 'Twitter', icon: Twitter, color: 'text-sky-400' },
  tiktok: { name: 'TikTok', icon: Music2, color: 'text-pink-400' },
};

const SPORTS = [
  { id: 'cricket', name: 'Cricket', emoji: '🏏' },
  { id: 'football', name: 'Football', emoji: '⚽' },
  { id: 'tennis', name: 'Tennis', emoji: '🎾' },
];

const IgamingAutoPost = () => {
  const [config, setConfig] = useState({
    enabled: false,
    account_ids: [],
    sports_enabled: true,
    sports: ['cricket', 'football', 'tennis'],
    casino_enabled: true,
    casino_frequency: 2,
    pre_match_mins_before: 60,
    timezone: 'Asia/Kolkata'
  });
  const [accounts, setAccounts] = useState([]);
  const [history, setHistory] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewType, setPreviewType] = useState('casino');
  const [previewSport, setPreviewSport] = useState('cricket');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [configRes, accountsRes, historyRes] = await Promise.all([
        axios.get(`${API}/igaming/config`),
        axios.get(`${API}/accounts`),
        axios.get(`${API}/igaming/history?limit=5`)
      ]);
      
      if (configRes.data && configRes.data.enabled !== undefined) {
        setConfig({
          enabled: configRes.data.enabled || false,
          account_ids: configRes.data.account_ids || [],
          sports_enabled: configRes.data.sports_enabled ?? true,
          sports: configRes.data.sports || ['cricket', 'football', 'tennis'],
          casino_enabled: configRes.data.casino_enabled ?? true,
          casino_frequency: configRes.data.casino_frequency || 2,
          pre_match_mins_before: configRes.data.pre_match_mins_before || 60,
          timezone: configRes.data.timezone || 'Asia/Kolkata'
        });
      }
      setAccounts(accountsRes.data || []);
      setHistory(historyRes.data?.history || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setAccounts([]);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (config.enabled && (!config.account_ids || config.account_ids.length === 0)) {
      toast.error('Please select at least one account');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/igaming/config`, config);
      toast.success('iGaming settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (contentType = 'casino', sport = 'cricket') => {
    if (!config.account_ids || config.account_ids.length === 0) {
      toast.error('Please select accounts and save first');
      return;
    }

    setTesting(true);
    try {
      const response = await axios.post(`${API}/igaming/test?content_type=${contentType}&sport=${sport}&post_type=pre_match`);
      toast.success('Test post sent!');
      setHistory(prev => [response.data, ...prev].slice(0, 5));
    } catch (error) {
      const msg = error.response?.data?.detail || 'Test failed';
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const response = await axios.get(`${API}/igaming/preview?content_type=${previewType}&sport=${previewSport}`);
      setPreview(response.data);
    } catch (error) {
      toast.error('Failed to generate preview');
    } finally {
      setPreviewing(false);
    }
  };

  const toggleAccount = (accountId) => {
    setConfig(prev => ({
      ...prev,
      account_ids: (prev.account_ids || []).includes(accountId)
        ? prev.account_ids.filter(id => id !== accountId)
        : [...(prev.account_ids || []), accountId]
    }));
  };

  const toggleSport = (sportId) => {
    setConfig(prev => ({
      ...prev,
      sports: prev.sports.includes(sportId)
        ? prev.sports.filter(s => s !== sportId)
        : [...prev.sports, sportId]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="igaming-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <Trophy className="w-8 h-8 text-yellow-500" />
          iGaming Auto-Post
        </h1>
        <p className="text-muted-foreground">
          Auto-post sports updates and casino trivia for Yolo247
        </p>
      </div>

      {/* Main Config */}
      <Card className="bg-card border-border/50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl">Enable iGaming Posts</CardTitle>
              <CardDescription>
                Auto-post cricket, football, tennis scores + casino trivia
              </CardDescription>
            </div>
            <Switch
              checked={config.enabled}
              onCheckedChange={(checked) => setConfig(prev => ({ ...prev, enabled: checked }))}
              data-testid="igaming-toggle"
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          
          {/* Account Selection */}
          <div className="space-y-3">
            <Label>Post to these accounts:</Label>
            {accounts.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {accounts.map((account) => {
                  const platformInfo = PLATFORM_INFO[account.platform];
                  const Icon = platformInfo?.icon || Send;
                  const isSelected = (config.account_ids || []).includes(account.id);
                  
                  return (
                    <div
                      key={account.id}
                      onClick={() => toggleAccount(account.id)}
                      className={cn(
                        "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all",
                        isSelected 
                          ? "border-primary bg-primary/10" 
                          : "border-border/50 hover:border-border"
                      )}
                    >
                      <Checkbox checked={isSelected} className="pointer-events-none" />
                      <Icon className={cn("w-4 h-4", platformInfo?.color)} />
                      <span className="font-medium">{account.account_name}</span>
                      <Badge variant="outline" className="ml-auto text-xs">
                        {platformInfo?.name}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                <p className="text-sm text-yellow-200 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  No accounts connected. Please connect Twitter/Telegram first.
                </p>
              </div>
            )}
          </div>

          {/* Sports Config */}
          <div className="space-y-3 pt-4 border-t border-border/50">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2">
                <Trophy className="w-4 h-4 text-yellow-500" />
                Sports Updates
              </Label>
              <Switch
                checked={config.sports_enabled}
                onCheckedChange={(checked) => setConfig(prev => ({ ...prev, sports_enabled: checked }))}
              />
            </div>
            
            {config.sports_enabled && (
              <div className="grid grid-cols-3 gap-2 pl-6">
                {SPORTS.map((sport) => (
                  <div
                    key={sport.id}
                    onClick={() => toggleSport(sport.id)}
                    className={cn(
                      "flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all",
                      config.sports.includes(sport.id)
                        ? "border-yellow-500 bg-yellow-500/10" 
                        : "border-border/50 hover:border-border"
                    )}
                  >
                    <span className="text-xl">{sport.emoji}</span>
                    <span className="font-medium">{sport.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Casino Config */}
          <div className="space-y-3 pt-4 border-t border-border/50">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2">
                <Dices className="w-4 h-4 text-purple-500" />
                Casino Trivia ("Did You Know?")
              </Label>
              <Switch
                checked={config.casino_enabled}
                onCheckedChange={(checked) => setConfig(prev => ({ ...prev, casino_enabled: checked }))}
              />
            </div>
            
            {config.casino_enabled && (
              <div className="pl-6">
                <p className="text-sm text-muted-foreground">
                  Posts at 10 AM IST daily - fascinating facts about Slots, Crash, Blackjack, Roulette, Poker & more
                </p>
              </div>
            )}
          </div>

          {/* Timezone */}
          <div className="space-y-2 pt-4 border-t border-border/50">
            <Label>Timezone</Label>
            <Select
              value={config.timezone}
              onValueChange={(value) => setConfig(prev => ({ ...prev, timezone: value }))}
            >
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Asia/Kolkata">IST (India)</SelectItem>
                <SelectItem value="Europe/London">GMT (London)</SelectItem>
                <SelectItem value="America/New_York">EST (New York)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-3 pt-4 border-t border-border/50">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Save Settings
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Preview & Test */}
      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="text-lg">Preview & Test</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <Select value={previewType} onValueChange={setPreviewType}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="casino">Casino</SelectItem>
                <SelectItem value="sports">Sports</SelectItem>
              </SelectContent>
            </Select>
            
            {previewType === 'sports' && (
              <Select value={previewSport} onValueChange={setPreviewSport}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cricket">Cricket 🏏</SelectItem>
                  <SelectItem value="football">Football ⚽</SelectItem>
                  <SelectItem value="tennis">Tennis 🎾</SelectItem>
                </SelectContent>
              </Select>
            )}
            
            <Button variant="outline" onClick={handlePreview} disabled={previewing}>
              {previewing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
              Preview
            </Button>
            
            <Button 
              variant="outline"
              onClick={() => handleTest(previewType, previewSport)} 
              disabled={testing || !config.account_ids || config.account_ids.length === 0}
              className="border-accent text-accent hover:bg-accent/10"
            >
              {testing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
              Test Post Now
            </Button>
          </div>

          {/* Preview Display */}
          {preview && (
            <div className="p-4 rounded-lg bg-muted/30 font-mono text-sm whitespace-pre-wrap">
              {preview.preview}
            </div>
          )}
        </CardContent>
      </Card>

      {/* History */}
      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <History className="w-5 h-5" />
            Recent Posts
          </CardTitle>
        </CardHeader>
        <CardContent>
          {history.length > 0 ? (
            <div className="space-y-3">
              {history.map((entry, index) => (
                <div 
                  key={entry.id || index}
                  className="p-4 rounded-lg bg-muted/30 border border-border/50"
                >
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <Badge variant={entry.content_type === 'casino' ? 'default' : 'secondary'}>
                      {entry.content_type === 'casino' ? '🎰 Casino' : '🏆 Sports'}
                    </Badge>
                    <span className="text-xs text-muted-foreground font-mono">
                      {entry.created_at && format(new Date(entry.created_at), 'MMM d, HH:mm')}
                    </span>
                  </div>
                  <p className="text-sm whitespace-pre-wrap line-clamp-3">{entry.content}</p>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {entry.results?.map((result, i) => (
                      <Badge 
                        key={i}
                        className={cn(
                          "text-xs",
                          result.status === 'success' 
                            ? "bg-accent/10 text-accent" 
                            : "bg-destructive/10 text-destructive"
                        )}
                      >
                        {result.platform}: {result.status}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <History className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p>No iGaming posts yet</p>
              <p className="text-sm">Use "Test Post Now" to try it out</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default IgamingAutoPost;
